import Head from 'next/head'
import { useEffect, useMemo, useContext, useState } from 'react'
import { withTranslation, Router } from 'server/i18n'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { Flex, Box } from 'reflexbox'
import slugify from 'slugify'
import useFetch from '@hmn/use-fetch'
import getConfig from 'next/config'
import ReactMarkdown from 'react-markdown'

import PageTitle from 'components/PageTitle'
import BoatProvider from 'components/BoatProvider'
import Loader from 'components/Loader'
import PhotoGallerySlider from 'components/PhotoGallerySlider'
import Dropdown from 'components/Dropdown'

import { getBoatsListingApiURL, getImageURL, getBoatLink } from 'helpers/middleware'
import { useBoatList } from 'helpers/hooks'
import { getBuyFields } from 'helpers/wordpress/wp-api'

import layoutTheme from 'theme/layout-theme'
import filters from 'components/BoatsFilterPanel/filters'
import AppContext from '../../contexts/AppContext'

const nullCheck = value => {
    if (value === 'NO_DATA') {
        return null
    }
    return value
}

const BoatsFilterPanel = dynamic(
    () => import(/* webpackChunkName: 'BoatsFilterPanel' */ 'components/BoatsFilterPanel'),
    {
        ssr: false
    }
)

const toSlug = value => {
    return slugify(`${value}`, { replacement: '-', lower: true })
}

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL, DOMAIN_ROOT } = publicRuntimeConfig

const alphabeticalSort = (a, b) => {
    const aString = `${a || ''}`.trim().toLowerCase()
    const bString = `${b || ''}`.trim().toLowerCase()
    if (aString < bString) {
        return -1
    }
    if (aString > bString) {
        return 1
    }
    return 0
}

const sortMethods = [
    {
        id: 'recently-added',
        isDefault: true,
        // eslint-disable-next-line no-underscore-dangle
        action: (a, b) => (b?.created_at?._seconds || 0) - (a?.created_at?._seconds || 0)
    },
    {
        id: 'year-oldest-first',
        action: (a, b) => (((a || {}).info || {}).year || 9999) - (((b || {}).info || {}).year || 9999)
    },
    {
        id: 'price-high-to-low',
        action: (a, b) => ((b || {}).price || {}).value - ((a || {}).price || {}).value
    },
    {
        id: 'price-low-to-high',
        action: (a, b) => ((a || {}).price || {}).value - ((b || {}).price || {}).value
    },
    { id: 'length-high-to-low', action: (a, b) => ((b || {}).info || {}).length - ((a || {}).info || {}).length },
    { id: 'length-low-to-high', action: (a, b) => ((a || {}).info || {}).length - ((b || {}).info || {}).length },
    {
        id: 'year-newest-first',
        action: (a, b) => (((b || {}).info || {}).year || 9999) - (((a || {}).info || {}).year || 9999)
    }
]

const Listing = ({ t, i18n, canonical, ssrBoatsListingData, seoTextEN, seoTextDE, welcheYachtEN, welcheYachtDE }) => {
    const { setNumberOfFilteredBoats } = useContext(AppContext)

    const { data: boatsListingData, isLoading: boatsListingDataPending, hasError: boatsListingDataError } = useBoatList(
        ssrBoatsListingData
    )
console.log(boatsListingData, 'boatsListingData')
    const { activeFilters } = useContext(AppContext)

    const activeSort = useMemo(() => {
        if (activeFilters.sort && activeFilters.sort.length) {
            const sortMethod = sortMethods.find(method => method.id === activeFilters.sort[0])
            if (sortMethod) {
                return sortMethod
            }
        }
        return sortMethods.find(method => method.isDefault)
    }, [activeFilters.sort])

    const filterData = useMemo(() => {
        return (Object.entries(activeFilters || {}) || [])
            .map(([key, values]) => {
                const filter = filters.find(item => item.filterType === key)
                if (key === 'brand' || key === 'brand_exact') {
                    return {
                        type: key,
                        selected: values
                    }
                }
                if (!filter) {
                    return null
                }
                return {
                    type: key,
                    selected: (values || [])
                        .map(value => {
                            const option = filter.options.find(o => toSlug(o.name) === value)
                            return option
                        })
                        .filter(option => option)
                }
            })
            .filter(filter => filter && filter.selected.length)
    }, [activeFilters])

    const featuredBoatsListingData = useMemo(() => {
        if (!boatsListingData || !boatsListingData.length || filterData.length || !activeSort.isDefault) {
            return []
        }

        const featuredList = boatsListingData.filter(boat => boat.published && boat.featured)

        if (featuredList.length < 3) {
            return []
        }

        // eslint-disable-next-line no-underscore-dangle
        featuredList.sort((boatA, boatB) => boatB?.created_at?._seconds - boatA?.created_at?._seconds)

        return featuredList.map(({ id, attachments, info, slug, price }) => ({
            id,
            url: getImageURL((attachments || {}).images, { position: 'listing', size: 'featured', hasFallback: true }),
            name: info.model,
            condition: t(`controls:${info.condition}`),
            year: info.year,
            length: info.length,
            price: price.displayValue === '0' ? t('price-upon-request') : price.displayValue,
            vat: price.vat,
            boatURL: getBoatLink(info.type, slug, i18n.language).href,
            boatURLas: getBoatLink(info.type, slug, i18n.language).as,
            currency: price.displayValue === '0' ? '' : price.currency
        }))
    }, [t, boatsListingData, filterData, activeSort])

    const filteredBoatsListingData = useMemo(() => {
        if (!boatsListingData || !boatsListingData.length) {
            return []
        }

        const filteredList = boatsListingData
            .filter(boat => boat.published)
            .filter(boatData => {
                return filterData.reduce((validAnd, filter) => {
                    let validOr = true
                    if (['type'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = nullCheck(boatData.info[filter.type])
                            const valid = value === item.value
                            return valid
                        })
                    } else if (['condition'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = nullCheck(boatData.info[filter.type])
                            /**
                             * After condition updates, boats now return both condition and conditionCrm,
                             * we need to check for "Stock" keyword.
                             */
                            const valueCrm = nullCheck(boatData.info.conditionCrm)

                            if (item.value === 'new-stock') {
                                const itemValueNew = 'Stock'
                                const valid = valueCrm === itemValueNew
                                return valid
                            }
                            const valid = value === item.value
                            return valid
                        })
                    } else if (['brand'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = nullCheck(boatData.info[filter.type])
                            if (!value) {
                                return false
                            }
                            const brandValue = slugify(value || 'brand', { lower: true })
                            const brands = item
                                .split(',')
                                .map(brand => brand.trim().toLowerCase())
                                .filter(brand => brand)
                            const valid = brands.some(brand => brandValue === brand)
                            return valid
                        })
                    } else if (['brand_exact'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = nullCheck(boatData.info.brand)
                            const valid = value.toLowerCase().includes(item.trim().toLowerCase())
                            return valid
                        })
                    } else if (['year', 'length'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = parseFloat(nullCheck(boatData.info[filter.type])) || null
                            const valid = value >= item.min && value < item.max
                            return valid
                        })
                    } else if (['price'].includes(filter.type)) {
                        validOr = !!filter.selected.find(item => {
                            const value = parseFloat(nullCheck(boatData.price.value)) || null
                            const valid = value >= item.min && value < item.max
                            return valid
                        })
                    }
                    return validAnd && validOr
                }, true)
            })

        return filteredList.sort(activeSort.action)
    }, [boatsListingData, filterData, activeSort, featuredBoatsListingData])

    const brandList = useMemo(() => {
        if (!boatsListingData || !boatsListingData.length) {
            return []
        }
        const list = boatsListingData
            .filter(boat => boat.published)
            .reduce((all, boat) => {
                const { info: { brand: name } = {} } = boat
                if (name && !all.find(brand => brand.name === name)) {
                    all.push({
                        name,
                        featured: ['Cranchi Yachts', 'Princess Yachts', 'Solaris Power', 'Solaris Yachts'].includes(
                            name
                        )
                    })
                }
                return all
            }, [])
        list.sort((a, b) => alphabeticalSort(a.name, b.name))
        list.sort((a, b) => {
            if (a.featured && !b.featured) {
                return -1
            }
            if (!a.featured && b.featured) {
                return 1
            }
            return 0
        })
        return list.map(({ name, featured }, index) => ({
            id: index + 1,
            name,
            shortName: name,
            featured,
            value: slugify('brand' || name, { lower: true })
        }))
    }, [boatsListingData])

    const sortMethodOptions = useMemo(() => {
        return sortMethods.map(({ id }) => ({
            id,
            label: t(id)
        }))
    }, [i18n.language, t])

    useEffect(() => {
        setNumberOfFilteredBoats(filteredBoatsListingData.length)
    }, [boatsListingData, filteredBoatsListingData])

    const onSortChange = id => {
        // eslint-disable-next-line no-unused-vars
        const { sort, lng, subpath, ...otherFilters } = Router.query
        const defaultSortMethod = sortMethods.find(method => method.isDefault)

        const sortFilters = {}
        if (id !== defaultSortMethod.id) {
            sortFilters.sort = id
        }

        Router.push({
            pathname: Router.pathname,
            query: {
                ...otherFilters,
                ...sortFilters
            }
        })
    }

    const [boatsFilterPanelRandomTrigger, setBoatsFilterPanelRandomTrigger] = useState()

    const openBoatsFilterPanel = () => {
        setBoatsFilterPanelRandomTrigger(Math.random())
    }

    const seoData = {
        title: `${t('meta-data:buy-title')}`,
        description: t('meta-data:buy-description'),
        canonical: t('meta-data:buy-url'),
        openGraph: {
            title: `${t('meta-data:buy-title')}`,
            description: t('meta-data:buy-description'),
            url: t('meta-data:buy-url'),
            images: [
                {
                    url: `${CLOUDINARY_URL}/v1582626095/Static/buy-alt-2_f7md4v.jpg`
                }
            ]
        },
        languageAlternates: [
            {
                hrefLang: 'en',
                href: `${DOMAIN_ROOT}/listing`
            },
            {
                hrefLang: 'de',
                href: `${DOMAIN_ROOT}/de/listing`
            }
        ]
    }

    const getBoatKey = (boat, index) => {
        return `${(boat || {}).id || 'boat'}_${index}`
    }

    return (
        <div>
            <NextSeo {...seoData} />
            <Head>
                <script
                    language="javascript"
                    type="text/javascript"
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                        __html: `(function (id, f, t, ws, ms_tr_il_08, ms_tr_il_w_01) { var tr = function (cb) { var count = 0; var callback = function () { if (count == 0) { count++; if (w) { w.w(id, t, cb); } } }; var ts = document.createElement('script'); ts.src = ws; ts.type = 'text/javascript'; ts.onload = callback; ts.onreadystatechange = function () { if (this.readyState == 'complete' || this.readyState == 'loaded') { callback(); } }; var head = document.getElementsByTagName('head')[0]; head.appendChild(ts); }; if (typeof ms_tr_il_08 === 'function') { if (ms_tr_il_w_01 === null) { tr(function() { ms_tr_il_08(id, f, t); }); } else { ms_tr_il_w_01.w(id, t, function(websiteVisitedParams) { ms_tr_il_08(id, f, t, websiteVisitedParams); }, null); } } else { tr(); }})('7c7sigT3WaNKuGDI6pnnylCwRMo83uZqVuqqFtd-FlU', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/f', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t/w', typeof ms_tr_il_08 === "undefined" ? null : ms_tr_il_08, typeof ms_tr_il_w_01 === "undefined" ? null : ms_tr_il_w_01);`
                    }}
                />
            </Head>

            <Flex
                theme={layoutTheme}
                flexWrap="wrap"
                justifyContent="space-between"
                maxWidth={1600}
                mx="auto"
                px={['40px', null, null, null, null, 0]}
                mt={['80px', null, null, '140px']}
                mb={['58px', null, null, '58px']}>
                <Box
                    theme={layoutTheme}
                    width={[1, null, null, 1, '1280px']}
                    mb={[0, 0, 0, '40px']}
                    mx={[0, 0, 0, 0, 'auto']}>
                    <PageTitle
                        title={t('new-and-preowned-yachts-for-sale')}
                        subtitle={t('new-and-preowned-yachts-for-sale-alt')}
                        isOnListing
                        titleTag="h1"
                        subtitleTag="h2"
                        subtitleOnClick={openBoatsFilterPanel}
                    />
                </Box>
            </Flex>

            <Flex
                theme={layoutTheme}
                maxWidth={1600}
                px={['20px', null, null, null, null, 0]}
                mx="auto"
                mb={18}
                flexDirection="column">
                <Box theme={layoutTheme} mb={20}>
                    {process.browser ? (
                        <BoatsFilterPanel randomTrigger={boatsFilterPanelRandomTrigger} brandList={brandList} />
                    ) : null}
                </Box>

                {process.browser ? (
                    <Dropdown
                        label={`${t('sort-by')}:`}
                        value={activeSort.id}
                        onChange={e => onSortChange(e.target.value)}
                        options={sortMethodOptions}
                        isSort
                    />
                ) : null}
            </Flex>

            {featuredBoatsListingData.length > 0 && (
                <Box
                    theme={layoutTheme}
                    bg="#003D5A"
                    pt={[40, null, null, null, 80]}
                    pb={[40, null, null, null, 0]}
                    mb={80}>
                    <Box
                        theme={layoutTheme}
                        fontSize={[28, null, null, null, 40]}
                        fontWeight={500}
                        mb={30}
                        pl={[20, null, null, 40, null, 160]}>
                        {t('special-yacht-offers')}
                    </Box>

                    <Box theme={layoutTheme} mb={[60, null, 100, 200]}>
                        <PhotoGallerySlider images={featuredBoatsListingData} specialOffers />
                    </Box>
                </Box>
            )}

            <Box theme={layoutTheme} variant="center1600" mb={80} px={[20, null, null, null, null, 0]}>
                <PageTitle title={t('all-yacht-offers')} titleTag="h2" />
            </Box>

            {boatsListingDataError || boatsListingDataPending ? (
                <Loader height="40vh" />
            ) : (
                <Flex
                    theme={layoutTheme}
                    flexDirection={['column', null, 'row']}
                    flexWrap="wrap"
                    justifyContent="space-between"
                    maxWidth={1600}
                    mb={['100px', null, null, '300px']}
                    mx="auto"
                    px={['20px', null, null, null, null, 0]}
                    variant="spaceBetweenLastRow">
                    {filteredBoatsListingData.length ? (
                        filteredBoatsListingData.map((boat, index) => (
                            <BoatProvider
                                key={getBoatKey(boat, index)}
                                index={index}
                                boat={boat}
                                published={boat.published}
                                welcheYachtBox={i18n.language === 'en' ? welcheYachtEN : welcheYachtDE}
                            />
                        ))
                    ) : (
                        <Box
                            theme={layoutTheme}
                            width={[3 / 4, null, null, 1, '1280px']}
                            mb={[0, 0, 0, '40px']}
                            mx={[0, 0, 0, 0, 'auto']}>
                            <div style={{ textAlign: 'center' }}>{t('no-results')}</div>
                        </Box>
                    )}
                </Flex>
            )}

            {seoTextDE && (
                <Box theme={layoutTheme} px={20} maxWidth={1440} mx="auto" mb={[80, null, null, 150, 250]}>
                    <Box theme={layoutTheme} mb={80} px={[20, null, null, null, null, 0]}>
                        <ReactMarkdown source={i18n.language === 'de' ? seoTextDE : seoTextEN} escapeHtml={false} />
                    </Box>
                </Box>
            )}
        </div>
    )
}

Listing.getInitialProps = async ({ query, req: { i18n } = {} }) => {
    const { brand } = query
    const url = getBoatsListingApiURL()
    const buyPageAcf = await getBuyFields()
    const ssrBoatsListingData = !process.browser
        ? await useFetch.backendService.handleResponse(await fetch(url))
        : undefined

    const seoTextEN = buyPageAcf?.buys?.nodes[0]?.acf?.seoOptimisedTextEn
    const welcheYachtEN = buyPageAcf?.buys?.nodes[0]?.acf?.welcheYachtBoxEn
    const seoTextDE = buyPageAcf?.buys?.nodes[0]?.acf?.seoOptimisedTextDe
    const welcheYachtDE = buyPageAcf?.buys?.nodes[0]?.acf?.welcheYachtBoxDe
    return {
        brand,
        seoTextEN,
        seoTextDE,
        welcheYachtEN,
        welcheYachtDE,
        canonical: i18n ? i18n.getFixedT('en')('meta-data:') : undefined,
        ssrBoatsListingData:
            !process.browser && !ssrBoatsListingData.isError ? ssrBoatsListingData.fetchedResult : undefined,
        namespacesRequired: ['common', 'meta-data']
    }
}

Listing.propTypes = {
    seoTextEN: PropTypes.string,
    seoTextDE: PropTypes.string,
    welcheYachtEN: PropTypes.shape({
        title: PropTypes.string,
        text: PropTypes.string,
        image: PropTypes.shape({})
    }),
    welcheYachtDE: PropTypes.shape({
        title: PropTypes.string,
        text: PropTypes.string,
        image: PropTypes.shape({})
    }),
    canonical: PropTypes.string,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    ssrBoatsListingData: PropTypes.oneOfType([() => null, PropTypes.object])
}

Listing.defaultProps = {
    seoTextEN: undefined,
    welcheYachtEN: {},
    seoTextDE: undefined,
    welcheYachtDE: {},
    canonical: undefined,
    ssrBoatsListingData: []
}

export default withTranslation('common', 'meta-data')(Listing)
